import { calculateChangeTracking } from 'Data/Core/ChangeTracker';
import * as iop from 'Lib/Object/Path/Immutable';
import { IApiDataModel } from 'Data/ViewModels/IApiDataModel';
import { pipe, filter, map, always } from 'ramda';
import { IWorksheetComponent } from 'Components/Worksheets/Core';
import { FieldLabelText } from '../Field';
import { WorksheetGridEntryInjectedPropsProvider } from 'Components/WorksheetGrids/Core/WorksheetGridEntryInjectedPropsProvider';
import TextOutput from '../TextOutput';
import { getConfigValue } from 'Components/Worksheets/Core/Helpers/configHelpers';
import { GridDefinition } from 'Components/WorksheetGrids';

const toReadablePath = (path: string) => path.replace(/\.value$/, ''); // Remove trailing .value

const toReadableValue = (path: string, value: any): string => {
  let readable: string;

  if (Array.isArray(value) && value.length > 0) {
    // Support displaying simple arrays of values that can be shown as text without much effort
    readable = `[${value.map((item) => (item?.value !== undefined ? item.value : item)).join(', ')}]`;
  } else {
    if (value?.value !== undefined) value = value.value;
    readable = value?.toString();
  }

  if (readable) {
    if (readable.includes('[object')) readable = '(multiple)';
  } else {
    // If path ends with [*] then it's probably a deleted array item, not a cleared value
    if (/\[.*\]$/.test(path)) readable = '(deleted)';
  }

  return readable;
};

// Contents component is separate so changing the sync model doesn't cause this to render repeatedly.
interface ISyncDialogContents extends IWorksheetComponent {
  clientModel: IApiDataModel | undefined;
  serverModel: IApiDataModel | undefined;
  fieldPath?: string | undefined; // A path of a field that we want to explicitly compare
}

const WorksheetChangesGrid = ({ clientModel, serverModel, fieldPath }: ISyncDialogContents) => {
  const { changeTracking } = calculateChangeTracking(clientModel, serverModel);
  const clientConflictPaths = Object.keys(changeTracking);

  return pipe(
    always(clientConflictPaths),
    filter((path: string) =>
      fieldPath
        ? path === fieldPath
        : !iop.parse(path).some((key) => key === 'Conflicts' || key === 'Messages' || key === 'Limits')
    ),
    map((path) => {
      const client: string = toReadableValue(path, iop.get(clientModel, path));
      const server: string = toReadableValue(path, iop.get(serverModel, path));
      path = toReadablePath(path);
      return [
        <FieldLabelText name={path} />,
        <TextOutput name={path} value={client} />,
        <TextOutput name={path} value={server} />,
      ];
    })
  )();
};

interface IGridSyncDialogContents extends IWorksheetComponent {
  worksheets: any[];
  gridDefinition: GridDefinition;
}
const GridMassEntryChangesGrid = ({ worksheets, gridDefinition }: IGridSyncDialogContents): any[][] => {
  const gridSettings = gridDefinition.syncDialogSettings;
  return worksheets
    .flatMap((w) => {
      const clientModel = w.syncModel?.data;
      const serverModel = w.syncModel_synced?.data;
      const config = w.config;
      const { changeTracking } = calculateChangeTracking(clientModel, serverModel);
      const clientConflictPaths = Object.keys(changeTracking);

      return pipe(
        always(clientConflictPaths),
        filter(
          (path: string) =>
            !gridSettings.excludedPaths.some((p) => path.startsWith(p)) &&
            !iop.parse(path).some((key) => key === 'Conflicts' || key === 'Messages' || key === 'Limits')
        ),
        map((path) => {
          const client: string = toReadableValue(path, iop.get(clientModel, path));
          const server: string = toReadableValue(path, iop.get(serverModel, path));
          const format = getConfigValue(config, `data.${path}`)?.format;
          path = toReadablePath(path);
          return [
            <WorksheetGridEntryInjectedPropsProvider worksheetId={clientModel.Id}>
              <TextOutput name={gridSettings.identifierPath} />
            </WorksheetGridEntryInjectedPropsProvider>,
            <WorksheetGridEntryInjectedPropsProvider worksheetId={clientModel.Id}>
              <FieldLabelText name={`data.${path}`} />
            </WorksheetGridEntryInjectedPropsProvider>,
            <WorksheetGridEntryInjectedPropsProvider worksheetId={clientModel.Id}>
              <TextOutput name={`data.${path}`} value={client} />
            </WorksheetGridEntryInjectedPropsProvider>,
            <WorksheetGridEntryInjectedPropsProvider worksheetId={clientModel.Id}>
              <TextOutput format={format} value={server} />
            </WorksheetGridEntryInjectedPropsProvider>,
          ];
        })
      )();
    })
    .filter((s) => !!s);
};

export { WorksheetChangesGrid, GridMassEntryChangesGrid };
